import {
  type Entity,
  getSessionId,
  init,
  type LogClient,
  type Metric,
  type MonsterversePerfStats,
  type ProductEvent,
  setSessionId,
} from "@classdojo/log-client";
import type { AppFlag, PerformanceTier, UNKNOWN_PERFORMANCE_TIER } from "@monsterverse/app-types";
import { IslandType, type UserAuthType } from "@monsterverse/island-types";
import pick from "lodash/pick";

import { type AppMetadata, appVersionFromUserAgent, platformFromUserAgent } from "../appBridge/AppMetadata";
import { UserManager } from "../controllers/UserManager";
import { getLauncherStartupOptions } from "../controllers/Utility";
import { USER_FEATURE_SWITCHES } from "../featureSwitches/constants";
import type { IFeatureSwitchManager } from "../featureSwitches/IFeatureSwitchManager";
import { AutoFlushingQueue } from "../utils/AutoFlushingQueue";
import { Env } from "../utils/env";
import { convertToSeconds } from "../utils/NumberExtension";
import { createWindowTimerFunctions } from "../utils/Timer";
import type { DiagnosticMetadata } from "./DiagnosticMetadata";

export type { MonsterversePerfStats };

type LogMethod = "info" | "warn" | "error" | "exception";

type LogMessageOptions = {
  logOnce?: boolean;
  addDiagnosticMetadata?: boolean;
};

export class Logger {
  private static _logClient: LogClient;
  private static _featureSwitchManager: IFeatureSwitchManager;
  static appMetadata: AppMetadata = {};
  private static _fleetIQInstanceID?: string;
  private static _serverID?: string;
  private static _serverGameSessionId?: string;
  private static _appFlag?: AppFlag;
  private static _islandType?: string;
  private static _insidePWOwnerId?: string;
  private static _lastEnteredPWSeconds?: number;
  private static _performanceTier?: PerformanceTier | typeof UNKNOWN_PERFORMANCE_TIER;
  private static _countryCode?: string;
  private static _playerCoordinates?: { x: number; y: number; z: number };
  private static _authType?: string;
  private static _connectedUsers?: number;
  private static _connectedGuests?: number;
  private static _activityId?: string;
  private static _webGlVersion?: string;
  private static _webGlContextLost?: boolean;
  private static _windowVisible?: boolean;
  private static _windowVisibleChanged?: number;
  private static _ugcReportModalVisible: boolean;
  private static _appReadyTime = -1;
  private static _frame = -1;
  private static _activateStateGraphs = 0;
  private static _lastProductEventSent: ProductEvent | undefined;
  private static _lastProductEventTime = -1;
  private static readonly _undefinedStr = "--";
  private static _eventsQueue: AutoFlushingQueue<ProductEvent>;
  private static _perfStatsQueue: AutoFlushingQueue<MonsterversePerfStats>;
  private static _metricsQueue: AutoFlushingQueue<Metric>;
  private static _hostAppVersion?: string;
  private static _classYear?: string;
  private static _additionalLogContext: Record<string, unknown> | undefined;
  private static _diagnosticMetadata: DiagnosticMetadata = {};

  // eslint-disable-next-line sonarjs/cognitive-complexity
  static initialize(
    featureSwitchManager: IFeatureSwitchManager,
    options: {
      env: Env;
      customEndpoint?: string;
      buildNumber?: string;
      appVersion?: string;
      server?: boolean;
      disabledForTest?: boolean;
      sessionId?: string;
      entityId?: string;
    }
  ) {
    const { env, customEndpoint, buildNumber, appVersion, server, disabledForTest, sessionId, entityId } = options;
    this._featureSwitchManager = featureSwitchManager;
    if (!Logger._logClient) {
      Logger._logClient = init({
        initNewInstance: true,
        env: env === Env.Prod ? "prod" : "dev",
        site: "monsterworld",
        customEndpoint,
        buildNumber: buildNumber || window.build,
        appVersion: appVersion || window.build,
        devParseStubbedLogs: true,
        disabledForTest,
        sessionId,
        entityId,
      });
      Logger._eventsQueue = new AutoFlushingQueue<ProductEvent>(60000, 50, (logs) => {
        try {
          Logger._logClient.logMonsterverseProductEvents(logs);
        } catch {
          Logger.logException(new Error("Failed to logMonsterverseEvents"));
        }
      });
      Logger._perfStatsQueue = new AutoFlushingQueue<MonsterversePerfStats>(60000, 50, (stats) =>
        Logger._logClient.logMonsterversePerfStats(stats)
      );
      Logger._metricsQueue = new AutoFlushingQueue<Metric>(60000, 50, (metrics) => Logger.logMetrics(metrics));
      Logger._hostAppVersion = appVersionFromUserAgent();

      if (typeof document !== "undefined" && document?.addEventListener) {
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") {
            Logger.flushLogs();
          }
        });
      }

      //Disable most console logging when in production (only keeping error for debugging purposes - and its very limited amount of prints)
      if (env === Env.Prod && !server) {
        const ignoreLogs = () => {
          //NOOP
        };
        console.log = ignoreLogs;
        console.info = ignoreLogs;
        console.warn = ignoreLogs;
        console.debug = ignoreLogs;
        window.time = ignoreLogs;
        window.timeEnd = ignoreLogs;
      } else {
        createWindowTimerFunctions();
      }
    }

    Logger._fleetIQInstanceID = undefined;
    Logger._serverID = undefined;
    Logger._serverGameSessionId = undefined;
    Logger._appFlag = undefined;
    Logger._islandType = undefined;
    Logger._insidePWOwnerId = undefined;
    Logger._lastEnteredPWSeconds = undefined;
    Logger._performanceTier = undefined;
    Logger._countryCode = undefined;
    Logger._playerCoordinates = undefined;
    Logger._connectedUsers = undefined;
    Logger._connectedGuests = undefined;
    Logger._webGlVersion = undefined;
    Logger._webGlContextLost = undefined;
    Logger._windowVisible = undefined;
    Logger._windowVisibleChanged = undefined;
    Logger._ugcReportModalVisible = false;
  }

  static setClassYear(year: string | undefined) {
    Logger._classYear = year;
  }
  static getClassYear() {
    return Logger._classYear;
  }
  static setAppReadyTime(time: number) {
    Logger._appReadyTime = time;
  }

  static get frame(): number {
    return Logger._frame;
  }
  static set frame(value: number) {
    Logger._frame = value;
  }

  static incActivateStateGraphs(): number {
    return ++Logger._activateStateGraphs;
  }
  static get activateStateGraphs(): number {
    return Logger._activateStateGraphs;
  }

  static setFeatureSwitches(featureSwitches: Record<string, string>) {
    Logger._logClient.featureSwitches = featureSwitches;
  }

  static setProductArea(productArea: string) {
    Logger._logClient.productArea = productArea;
  }

  static setServerFleetIQInstanceID(fleetIQInstanceID: string) {
    Logger._fleetIQInstanceID = fleetIQInstanceID;
  }

  static setServerID(serverID: string) {
    Logger._serverID = serverID;
  }

  static setServerGameSessionId(gameSessionId: string) {
    Logger._serverGameSessionId = gameSessionId;
  }

  static getServerGameSessionId() {
    return Logger._serverGameSessionId;
  }

  static setPerformanceTier(performanceTier: PerformanceTier | typeof UNKNOWN_PERFORMANCE_TIER) {
    Logger._performanceTier = performanceTier;
  }

  static setIslandType(islandType: string) {
    Logger._islandType = islandType;
  }

  static setInsidePWOwnerId(ownerId: string | undefined) {
    Logger._insidePWOwnerId = ownerId;
    // We only want to update the time if we're entering a PW
    Logger._lastEnteredPWSeconds = ownerId != undefined ? convertToSeconds(Date.now()) : Logger._lastEnteredPWSeconds;
  }

  static setAppFlag(appFlag: AppFlag) {
    Logger._appFlag = appFlag;
  }

  static setCountryCode(countryCode: string) {
    Logger._countryCode = countryCode;
  }

  static setPlayerCoordinates(coords: { x: number; y: number; z: number }) {
    Logger._playerCoordinates = coords;
  }

  static setConnectedUsers(users: number) {
    Logger._connectedUsers = users;
  }
  static get connectedUsers() {
    return Logger._connectedUsers ?? 0;
  }
  static setConnectedGuests(guests: number) {
    Logger._connectedGuests = guests;
  }
  static get connectedGuests() {
    return Logger._connectedGuests ?? 0;
  }

  static setWebGlVersion(webGlVersion: string) {
    Logger._webGlVersion = webGlVersion;
  }

  static setWebGlContextLost(webGlContextLost: boolean) {
    Logger._webGlContextLost = webGlContextLost;
  }

  static setWindowVisibility(visible: boolean) {
    Logger._windowVisible = visible;
    Logger._windowVisibleChanged = Date.now();
  }

  static setUgcReportModalVisible(visible: boolean) {
    Logger._ugcReportModalVisible = visible;
  }

  static setActivityId(activityId: string) {
    Logger._activityId = activityId;
  }

  static setCurrentPlayerCount(numPlayers: number) {
    Logger._diagnosticMetadata.currentPlayerCount = numPlayers;
  }

  static setChunkVisibleCount(numChunks: number) {
    Logger._diagnosticMetadata.chunkVisibleCount = numChunks;
  }

  static setVertexCount(numVertices: number) {
    Logger._diagnosticMetadata.vertexCount = numVertices;
  }

  static setDrawCallCount(numDrawCalls: number) {
    Logger._diagnosticMetadata.drawCallCount = numDrawCalls;
  }

  static setVramUsageTotal(numVramBytes: number) {
    Logger._diagnosticMetadata.vramUsageTotal = numVramBytes;
  }

  static setIncomingNetworkSessionTotal(numNetworkSessionBytes: number) {
    Logger._diagnosticMetadata.incomingNetworkSessionTotal = numNetworkSessionBytes;
  }

  static setOutgoingNetworkSessionTotal(numNetworkSessionBytes: number) {
    Logger._diagnosticMetadata.outgoingNetworkSessionTotal = numNetworkSessionBytes;
  }

  static setHomeIslandAssignedPlayerPlotCount(numPlots: number) {
    Logger._diagnosticMetadata.homeIslandAssignedPlayerPlotCount = numPlots;
  }

  static setHomeIslandGrowthSize(numPlots: number) {
    Logger._diagnosticMetadata.homeIslandGrowthSize = numPlots;
  }

  static getIslandType() {
    // First check if island type has been explicitly set
    let islandType = Logger._islandType;

    // If not set directly, try to extract it from the server ID
    if (!islandType && Logger._serverID) {
      const serverIdParts = Logger._serverID.split("-", 1);

      if (serverIdParts.length > 0) {
        const extractedType = serverIdParts[0];
        islandType = `extracted:${extractedType}`;
      }
    }

    // Fall back to "unknown" if we couldn't determine the type
    if (!islandType) {
      islandType = "unknown";
    }

    return islandType;
  }

  static getClassID() {
    if (Logger._islandType === IslandType[IslandType.Class]) {
      return Logger._serverID?.replace(/^class-/, "");
    }
    return undefined;
  }

  static logEvent(
    rawData: string | ProductEvent,
    {
      /** Appends the entity type to the event name, such as "teacher" or "student". */
      appendEntityType = true,
      /** Appends additional metadata for debugging reliability and performance issues. */
      addDiagnosticMetadata = false,
    } = {}
  ) {
    if (typeof rawData === "string") {
      rawData = { eventName: rawData };
    }

    Logger._lastProductEventSent = rawData;
    Logger._lastProductEventTime = Date.now();

    if (Logger._fleetIQInstanceID) {
      rawData.metadata = { ...rawData.metadata, fleetIQInstanceID: Logger._fleetIQInstanceID };
    }
    if (Logger._serverID) {
      rawData.metadata = { ...rawData.metadata, serverID: Logger._serverID };
    }
    if (Logger._serverGameSessionId) {
      rawData.metadata = { ...rawData.metadata, serverGameSessionId: Logger._serverGameSessionId };
    }
    if (Logger.appMetadata.hostId) {
      rawData.metadata = { ...rawData.metadata, hostId: Logger.appMetadata.hostId };
    }

    if (Logger._appFlag) {
      rawData.metadata = { ...rawData.metadata, appFlag: Logger._appFlag };
    }

    if (Logger._performanceTier) {
      rawData.metadata = { ...rawData.metadata, performanceTier: Logger._performanceTier };
    }

    if (Logger._islandType) {
      rawData.metadata = { ...rawData.metadata, islandType: Logger._islandType };

      // For Class islands, also log the classID and island state
      if (Logger._islandType === IslandType[IslandType.Class]) {
        rawData.metadata = { ...rawData.metadata, classIslandState: { activityId: Logger._activityId } };

        if (Logger._serverID) {
          const classId = Logger.getClassID();
          rawData.metadata = { ...rawData.metadata, classId };
        }
      }
    }

    if (Logger._insidePWOwnerId) {
      rawData.metadata = { ...rawData.metadata, insidePWOwnerId: Logger._insidePWOwnerId };
    }

    if (Logger._lastEnteredPWSeconds) {
      let lastEnteredPWSeconds;
      if (Logger._lastEnteredPWSeconds != undefined) {
        lastEnteredPWSeconds = convertToSeconds(Date.now()) - Logger._lastEnteredPWSeconds;
      }
      rawData.metadata = { ...rawData.metadata, lastEnteredPWSeconds };
    }

    if (Logger._authType) {
      rawData.metadata = { ...rawData.metadata, authType: Logger._authType };
    }

    if (Logger._countryCode) {
      rawData.metadata = { ...rawData.metadata, countryCode: Logger._countryCode };
    }
    if (Logger._playerCoordinates) {
      rawData.metadata = {
        ...rawData.metadata,
        coordinates: { ...Logger._playerCoordinates },
      };
    }
    if (Logger._connectedUsers != undefined) {
      rawData.metadata = { ...rawData.metadata, connectedUsers: Logger._connectedUsers };
    }
    if (Logger._connectedGuests != undefined) {
      rawData.metadata = { ...rawData.metadata, connectedGuests: Logger._connectedGuests };
    }
    if (Logger._webGlVersion) {
      rawData.metadata = { ...rawData.metadata, webGlVersion: Logger._webGlVersion };
    }
    if (Logger._webGlContextLost !== undefined) {
      rawData.metadata = { ...rawData.metadata, webGlContextLost: Logger._webGlContextLost };
    }
    if (Logger._windowVisible !== undefined) {
      rawData.metadata = { ...rawData.metadata, windowVisible: Logger._windowVisible };
    }
    if (Logger._windowVisibleChanged !== undefined) {
      rawData.metadata = {
        ...rawData.metadata,
        windowVisibleChanged: convertToSeconds(Date.now() - Logger._windowVisibleChanged),
      };
    }

    if (addDiagnosticMetadata) {
      // get diagnostics metadata
      rawData.metadata = {
        ...rawData.metadata,
        diagnostics: this._getDiagnosticMetadata(),
      };
    }

    const entityTypePrefix = Logger._getEntityTypePrefix(rawData.eventName, appendEntityType);

    const platform = this.getPlatform();
    const eventName = `${platform}.${entityTypePrefix}${rawData.eventName}`;
    rawData.eventName = eventName;

    this._eventsQueue.push({
      clientCreatedAt: new Date(),
      ...pick(this.appMetadata, "osVersion", "device", "deviceID", "deviceModel"),
      ...rawData,
    });
  }

  public static getPlatform(): string {
    // sets the platform to a sensible default based on the user agent until metadata is synced
    const defaultPlatform = platformFromUserAgent();
    const platform = this.appMetadata?.platform || defaultPlatform;
    return platform.toLocaleLowerCase();
  }

  private static _getEntityTypePrefix(eventName: string, appendEntityType: boolean) {
    let entityTypePrefix = "";
    if (appendEntityType) {
      if (eventName.startsWith("teacher.") || eventName.startsWith("student.")) {
        throw new Error(
          `Don't use 'student.' or 'teacher.' prefixes for ${eventName}. They will be added based on the entityType`
        );
      }

      if (!Logger._logClient.entityType) {
        // try to get entity type from startup query string params
        const { entityType } = getLauncherStartupOptions();
        entityTypePrefix = `${entityType}.`;
      } else {
        entityTypePrefix =
          Logger._logClient.entityType === "studentUser" ? "student." : `${Logger._logClient.entityType}.`;
      }
    }

    return entityTypePrefix;
  }

  static shouldLogExperiment(featureSwitch: string) {
    // ignore experiments for other platforms
    if (!this._featureSwitchManager.isValidFeatureSwitchForCurrentPlatform(featureSwitch)) {
      return false;
    }

    // if we fetched the feature switch, we should def log it
    if (this._featureSwitchManager.featureSwitches && this._featureSwitchManager.featureSwitches[featureSwitch]) {
      return true;
    }

    // Otherwise, we didn't fetch the FS, so don't log any experiment conversions.
    // If it's a user switch, that should never happen, so log a warning.
    if (Object.values(USER_FEATURE_SWITCHES).includes(featureSwitch)) {
      Logger.logWarn(`User Feature Switch ${featureSwitch} was not fetched`);
    }

    return false;
  }

  static logStartExperiment(experiment: string) {
    // ignore experiments for other platforms
    if (Logger.shouldLogExperiment(experiment)) {
      // console.log("%c logStartExperiment", "color: blue;", experiment);
      Logger._logClient.logStartExperiment(experiment);
    }
  }

  static logStartExperimentWithOverrides(experiment: string, variant: string, entityId: string, entityType: Entity) {
    if (this._featureSwitchManager.featureSwitches && this._featureSwitchManager.featureSwitches[experiment]) {
      console.log("%c logStartExperimentWithOverrides", "color: blue;", experiment, variant, entityId, entityType);
      Logger._logClient.logStartExperimentWithOverrides(experiment, variant, entityId, entityType);
    }
  }

  static logConvertExperiment(experiment: string) {
    if (Logger.shouldLogExperiment(experiment)) {
      console.log("%c logConvertExperiment", "color: blue;", experiment);
      Logger._logClient.logConvertExperiment(experiment);
    }
  }

  static logConvertExperimentWithOverrides(experiment: string, variant: string, entityId: string, entityType: Entity) {
    if (this._featureSwitchManager.featureSwitches && this._featureSwitchManager.featureSwitches[experiment]) {
      console.log("%c logConvertExperimentWithOverrides", "color: blue;", experiment, variant, entityId, entityType);
      Logger._logClient.logConvertExperimentWithOverrides(experiment, variant, entityId, entityType);
    }
  }

  static setAdditionalLogContext(context: Record<string, unknown>) {
    Logger._additionalLogContext = context;
  }

  private static _generateLogContext(context: Record<string, unknown>) {
    return { ...Logger._additionalLogContext, ...context };
  }

  /** Get a copy of available diagnostic metadata. */
  private static _getDiagnosticMetadata(): DiagnosticMetadata {
    return { ...this._diagnosticMetadata };
  }

  static logException(error: Error, context: Record<string, unknown> = {}) {
    if (window.iosCancelButtonPressed) {
      return;
    }

    Logger._logClient.logException(error, this._getExtendedLogContext(this._generateLogContext(context)));
  }

  static logInfo(message: string, context: Record<string, unknown> = {}, options?: LogMessageOptions) {
    if (Logger._logMessage("info", message, this._generateLogContext(context), options)) {
      console.log(
        message,
        this._generateLogContext(context),
        options?.addDiagnosticMetadata && this._getDiagnosticMetadata()
      );
    }
  }

  static logWarn(message: string, context: Record<string, unknown> = {}, options?: LogMessageOptions) {
    if (Logger._logMessage("warn", message, this._generateLogContext(context), options)) {
      console.warn(
        message,
        this._generateLogContext(context),
        options?.addDiagnosticMetadata && this._getDiagnosticMetadata()
      );
    }
  }

  static logError(message: string, context: Record<string, unknown> = {}, options?: LogMessageOptions) {
    if (Logger._logMessage("error", message, this._generateLogContext(context), options)) {
      console.error(
        message,
        this._generateLogContext(context),
        options?.addDiagnosticMetadata && this._getDiagnosticMetadata()
      );
    }
  }

  private static _loggedMessages: Set<string> = new Set();
  private static _logMessage(
    verbosity: "error" | "warn" | "info",
    message: string,
    context: Record<string, unknown>,
    options: LogMessageOptions = {
      logOnce: false,
      addDiagnosticMetadata: false,
    }
  ) {
    const { logOnce, addDiagnosticMetadata } = options;
    if (logOnce) {
      if (Logger._loggedMessages.has(`${verbosity}: ${message}`)) {
        return false;
      }
      Logger._loggedMessages.add(`${verbosity}: ${message}`);
    }

    Logger._logClient.logMonsterverseMessage({
      message,
      verbosity,
      context: this._getExtendedLogContext(context, addDiagnosticMetadata),
    });

    return true;
  }

  private static _getExtendedLogContext(context?: Record<string, unknown>, addDiagnosticMetadata?: boolean) {
    return {
      userAgent: window.navigator.userAgent,
      deviceModel: this.appMetadata.deviceModel,
      hostAppVersion: this._hostAppVersion,
      platform: Logger.getPlatform(),
      gpu: window.deviceGPU ?? "unknown",
      sessionId: getSessionId(),
      userType: UserManager.instance.user?.type,
      serverID: this._serverID,
      serverGameSessionId: this._serverGameSessionId,
      hostId: Logger.appMetadata.hostId,
      appFlag: this._appFlag,
      islandType: this._islandType,
      insidePWOwnerId: this._insidePWOwnerId,
      lastEnteredPWSeconds: this._lastEnteredPWSeconds,
      performanceTier: this._performanceTier,
      authType: this._authType,
      entityId: Logger._logClient.entityId,
      schoolID: UserManager.instance.getSchoolId(),
      countryCode: this._countryCode,
      connectedUsers: this._connectedUsers,
      connectedGuests: this._connectedGuests,
      frame: this._frame,
      stateGraphs: this._activateStateGraphs,
      webGlVersion: this._webGlVersion,
      webGlContextLost: this._webGlContextLost,
      windowVisible: this._windowVisible,
      windowVisibleChanged: this._windowVisibleChanged
        ? convertToSeconds(Date.now() - this._windowVisibleChanged)
        : undefined,
      ugcReportModalVisible: this._ugcReportModalVisible,
      appState: {
        activityId: Logger._activityId,
        secondsSinceAppReady:
          this._appReadyTime > 0 ? convertToSeconds(Date.now() - this._appReadyTime) : this._undefinedStr,
        lastProductEvent: {
          event: this._lastProductEventSent,
          secondsSinceEvent: this._lastProductEventSent
            ? convertToSeconds(Date.now() - this._lastProductEventTime)
            : this._undefinedStr,
        },
        featureSwitches: this._featureSwitchManager.featureSwitches,
      },
      diagnostics: addDiagnosticMetadata ? this._getDiagnosticMetadata() : undefined,
      // NOTE: This is adding a lot of extra info to all our logs.
      // We decided to remove it for now. More context: https://classdojo.slack.com/archives/C05EPH2M0F5/p1714590671690819
      //...debugInfoSystem.getDebugInfo(),
      ...context,
    };

    // Some other values we may want to keep track of:

    // - total # of players
    // - local player coords
    // - memory usage (if available)
  }

  static printAppState() {
    console.log(this._getExtendedLogContext());
  }

  static logPerfStats(stats: MonsterversePerfStats) {
    stats.deviceModel = stats.deviceModel ?? this.appMetadata.deviceModel;
    if (Logger.appMetadata.hostId) {
      stats.hostId = Logger.appMetadata.hostId;
    }
    if (Logger._islandType === IslandType[IslandType.Home]) {
      // TODO: Refactor for sanity.
      // If you are here, you were having a hard time finding how Datadog knows "site" is "home".
      // YOU ARE LOOKING FOR THIS LINE: site === IslandType.Home
      // YOU ARE LOOKING FOR THIS LINE: site === "home"
      // YOU ARE LOOKING FOR THIS LINE: site = "home"
      stats.site = IslandType[IslandType.Home];
    }
    Logger._perfStatsQueue.push(stats);
  }

  /**
   * @summary Only throws an exception when working in developer mode, but still logs the exception to our logging services.
   * @remarks Optionally, use the logMethod parameter to change the verbosity of the reporting to the backend.
   * @param error Error object or string to be thrown.
   * @param logMethodOnProd Logging method to backend used to control what verbosity we wish to report.
   */
  static throwIfTesting(error: Error, logMethodOnProd?: LogMethod): void;
  static throwIfTesting(error: string, logMethodOnProd?: LogMethod): void;
  static throwIfTesting(error: Error | string, logMethodOnProd: LogMethod = "exception"): void {
    let throwable: Error | undefined;
    if (error instanceof Error) {
      throwable = error;
    } else if (typeof error === "string" || (error as unknown) instanceof String) {
      throwable = new Error(error);
    }

    if (throwable === undefined) {
      return;
    }

    if (Logger._logClient.env !== "prod") {
      throw throwable;
    }

    switch (logMethodOnProd) {
      default:
      case "info":
        Logger.logInfo(throwable.toString(), this._getExtendedLogContext({ ...throwable }));
        break;
      case "warn":
        Logger.logWarn(throwable.toString(), this._getExtendedLogContext({ ...throwable }));
        break;
      case "error":
        Logger.logError(throwable.toString(), this._getExtendedLogContext({ ...throwable }));
        break;
      case "exception":
        Logger.logException(
          throwable,
          this._getExtendedLogContext({
            ...throwable,
          })
        );
        break;
    }
  }

  static logMetrics(metrics: Metric[]) {
    if (metrics.length) {
      Logger._logClient?.logMetrics(metrics);
    }
  }

  static incrementMetric(metricName: string, value = 1, tags?: Record<string, string>, flushLogs = false) {
    this._metricsQueue.push({
      type: "increment",
      metricName,
      value,
      tags: {
        platform: Logger.getPlatform(),
        islandType: Logger.getIslandType(),
        ...tags,
      },
    });
    if (flushLogs) {
      Logger.flushLogs();
    }
  }

  static flushLogs() {
    Logger._eventsQueue.flush();
    Logger._perfStatsQueue.flush();
    Logger._metricsQueue.flush();
    Logger._logClient.flush();
  }

  static setEntityId(id?: string) {
    if (Logger._logClient) Logger._logClient.entityId = id;
  }

  static setAuthType(auth: UserAuthType) {
    if (Logger._logClient) Logger._authType = auth;
  }

  static setEntityType(entityType?: Entity) {
    if (Logger._logClient) Logger._logClient.entityType = entityType;
  }

  static getSessionId() {
    return getSessionId();
  }

  static setSessionId(sessionId: string) {
    setSessionId(sessionId);
  }
}
